/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { certsApi } from "./portal/shell";
import { Toaster } from "sonner";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import SEO from "../components/SEO";
import { CheckCircle2, ShieldAlert, ShieldOff, Search } from "lucide-react";

export default function Verify() {
  const params = new URLSearchParams(window.location.search);
  const [num, setNum] = useState(params.get("n") || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const check = async (e) => {
    e?.preventDefault();
    if (!num.trim()) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await certsApi.verify(num.trim());
      setResult(r);
    } catch (ex) {
      setErr(ex.message || "Lookup failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO
        title="Certificate Verification | Thunderbird Geospatial"
        description="Verify the authenticity of a Thunderbird Geospatial ground school certificate."
        canonical="https://tbirdgeo.com/verify/"
      />
      <Toaster theme="dark" position="top-right" />
      <main className="min-h-screen bg-tb-bg text-tb-text">
        <Navbar />
        <section className="pt-28 pb-24" data-testid="verify-page">
          <div className="max-w-2xl mx-auto px-6">
            <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
              Certificate Verification
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-3">
              Confirm a Thunderbird Geospatial certificate.
            </h1>
            <p className="text-tb-text/70 mb-8 text-sm sm:text-base leading-relaxed">
              Enter the certificate number printed on the PDF (or scan the QR code
              on it). This tool confirms whether a certificate was issued by
              Thunderbird Geospatial and whether it remains valid.
            </p>

            <form onSubmit={check} className="rounded-xl border border-white/10 bg-white/[0.02] p-6" data-testid="verify-form">
              <label className="block font-mono text-[10px] tracking-[0.2em] text-tb-text/60 uppercase mb-2">
                Certificate number
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={num}
                  onChange={(e) => setNum(e.target.value.toUpperCase())}
                  placeholder="TBG-YYYY-XXXX-XXXX"
                  className="flex-1 bg-black/40 border border-white/10 focus:border-tb-teal outline-none px-3 py-2.5 rounded-md text-sm font-mono transition"
                  data-testid="verify-input"
                />
                <button
                  type="submit"
                  disabled={busy || !num.trim()}
                  className="px-5 py-2.5 rounded-md bg-tb-teal text-black font-semibold hover:brightness-110 transition disabled:opacity-50 inline-flex items-center gap-2 text-sm"
                  data-testid="verify-submit"
                >
                  <Search size={14} /> {busy ? "Checking…" : "Verify"}
                </button>
              </div>
              {err && <div className="text-red-400 font-mono text-xs mt-3" data-testid="verify-error">{err}</div>}
            </form>

            {result && (
              <div className="mt-6" data-testid="verify-result">
                {result.valid && <ValidCard r={result} />}
                {!result.valid && result.revoked && <RevokedCard r={result} />}
                {!result.valid && !result.revoked && <InvalidCard num={result.number} />}
              </div>
            )}

            <p className="mt-10 text-tb-text/50 text-xs font-mono leading-relaxed">
              This tool checks certificates issued by Thunderbird Geospatial for
              our ground school courses only. Transport Canada Advanced Operations
              Pilot Certificates are issued by Transport Canada and verified through
              the Transport Canada Drone Management Portal.
            </p>
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}

function ValidCard({ r }) {
  return (
    <div className="rounded-xl border border-tb-teal/50 bg-tb-teal/[0.06] p-6" data-testid="verify-valid">
      <div className="flex items-center gap-3">
        <CheckCircle2 size={22} className="text-tb-teal" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-tb-teal uppercase">Certificate Valid</div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
        <Field k="Certificate No." v={r.number} mono />
        <Field k="Course" v={r.course_name} />
        <Field k="Issued to" v={r.student_name_masked} />
        <Field k="Issued on" v={r.issued_at ? new Date(r.issued_at).toISOString().slice(0, 10) : "—"} mono />
      </div>
    </div>
  );
}

function InvalidCard({ num }) {
  return (
    <div className="rounded-xl border border-red-400/40 bg-red-500/[0.06] p-6" data-testid="verify-invalid">
      <div className="flex items-center gap-3">
        <ShieldAlert size={22} className="text-red-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-red-400 uppercase">
          No matching certificate
        </div>
      </div>
      <p className="mt-3 text-sm text-tb-text/75 leading-relaxed">
        We could not find a certificate with number{" "}
        <span className="text-red-400 font-mono">{num}</span>. Double-check the
        number for typos, or contact us if you believe this is an error.
      </p>
      <Link
        to="/#quote"
        className="mt-4 inline-block font-mono text-[10px] tracking-[0.2em] text-tb-teal hover:text-white uppercase"
      >
        Report an issue →
      </Link>
    </div>
  );
}

function RevokedCard({ r }) {
  const issued = r.issued_at ? new Date(r.issued_at).toISOString().slice(0, 10) : "—";
  return (
    <div className="rounded-xl border border-amber-400/50 bg-amber-500/[0.06] p-6" data-testid="verify-revoked">
      <div className="flex items-center gap-3">
        <ShieldOff size={22} className="text-amber-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-amber-400 uppercase">
          Certificate revoked
        </div>
      </div>
      <p className="mt-3 text-sm text-tb-text/85 leading-relaxed">
        Certificate{" "}
        <span className="text-amber-400 font-mono">{r.number}</span> was issued
        by Thunderbird Geospatial but has since been revoked. It should not be
        accepted as valid proof of ground school completion.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
        <Field k="Certificate No." v={r.number} mono />
        <Field k="Course" v={r.course_name} />
        <Field k="Originally issued to" v={r.student_name_masked} />
        <Field k="Originally issued on" v={issued} mono />
        {r.revoked_reason && (
          <div className="sm:col-span-2">
            <div className="font-mono text-[9px] tracking-[0.22em] text-tb-text/50 uppercase">
              Reason for revocation
            </div>
            <div className="mt-1 text-sm text-amber-200">{r.revoked_reason}</div>
          </div>
        )}
      </div>
      <p className="mt-5 font-mono text-[10px] tracking-[0.05em] text-tb-text/55 leading-relaxed">
        If you're verifying this certificate for an employer or Transport Canada
        inspection, treat it as invalid. Reach out if you need documentation of
        the revocation for your records.
      </p>
    </div>
  );
}

function Field({ k, v, mono }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-[0.22em] text-tb-text/50 uppercase">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono text-sm text-tb-teal" : "text-sm text-white"}`}>{v || "—"}</div>
    </div>
  );
}
